import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Star, Award, Clock, Trophy, Medal } from "lucide-react";
import { useNavigate } from "react-router-dom";
import trainerCertifiedBadge from "@/assets/ekselencia-trainer-certified-badge.png";
import { BrandedImage } from "@/components/images/BrandedImage";

type ProfessionalType = "trainer" | "nutritionist" | "physiotherapist";

interface ProfessionalConfig {
  baseColor: string;
  hoverColor: string;
  iconColor: string;
  profilePath: string;
  directoryPath: string;
  joinPath: string;
  certBadgeAlt: string;
}

const PROFESSIONAL_CONFIG: Record<ProfessionalType, ProfessionalConfig> = {
  trainer: {
    baseColor: "from-primary/10 to-primary/5",
    hoverColor: "hover:bg-primary hover:text-primary-foreground",
    iconColor: "text-primary",
    profilePath: "/trainer",
    directoryPath: "/trainers",
    joinPath: "/become-trainer",
    certBadgeAlt: "Entrenador Certificado por Ekselencia.com",
  },
  nutritionist: {
    baseColor: "from-emerald-500/10 to-emerald-500/5",
    hoverColor: "hover:bg-emerald-500 hover:text-white",
    iconColor: "text-emerald-500",
    profilePath: "/nutritionist",
    directoryPath: "/nutritionists",
    joinPath: "/become-nutritionist",
    certBadgeAlt: "Nutricionista Certificado por Ekselencia.com",
  },
  physiotherapist: {
    baseColor: "from-rose-500/10 to-rose-500/5",
    hoverColor: "hover:bg-rose-500 hover:text-white",
    iconColor: "text-rose-500",
    profilePath: "/physiotherapist",
    directoryPath: "/physiotherapists",
    joinPath: "/become-physiotherapist",
    certBadgeAlt: "Fisioterapeuta Certificado por Ekselencia.com",
  },
};

const getMedalBySubscription = (subscriptionType: string) => {
  switch (subscriptionType) {
    case "lifetime":
      return { icon: Trophy, color: "text-yellow-500", label: "Elite" };
    case "yearly":
      return { icon: Medal, color: "text-blue-500", label: "Pro" };
    case "monthly":
      return { icon: Award, color: "text-green-500", label: "Verificado" };
    default:
      return { icon: Award, color: "text-gray-500", label: "Profesional" };
  }
};

interface ProfessionalCardProps {
  id: string;
  slug?: string;
  fullName: string;
  locationCity: string;
  locationCountry: string;
  specialty: string | string[];
  yearsExperience: number;
  hourlyRate: number;
  bio: string;
  profileImage?: string;
  subscriptionType?: string;
  type: ProfessionalType;
  source?: 'profile' | 'listing';
}

export const ProfessionalCard = ({
  id,
  slug,
  fullName,
  locationCity,
  locationCountry,
  specialty,
  yearsExperience,
  hourlyRate,
  bio,
  profileImage,
  subscriptionType,
  type,
  source = 'profile',
}: ProfessionalCardProps) => {
  const navigate = useNavigate();
  const config = PROFESSIONAL_CONFIG[type];
  const medal = getMedalBySubscription(subscriptionType || "free");
  const MedalIcon = medal.icon;
  
  // Determine the correct profile link based on source
  const profileLink = source === 'listing' 
    ? `/listing/${slug || id}` 
    : `${config.profilePath}/${slug || id}`;

  // Normalize specialty to always be an array
  const specialtyArray = Array.isArray(specialty) 
    ? specialty 
    : (specialty ? [specialty] : []);

  return (
    <Card className="hover:shadow-lg transition-shadow overflow-hidden">
      <CardHeader className="p-0 relative">
        <BrandedImage
          src={profileImage}
          category={type}
          alt={fullName}
          aspectRatio="aspect-square"
          logoSize="md"
          logoPosition="bottom-right"
        />
        <div className="absolute top-3 right-3 flex flex-col gap-2 items-end">
          <Badge className={`${medal.color} bg-white/90 backdrop-blur z-20`}>
            <MedalIcon className="w-4 h-4 mr-1" />
            {medal.label}
          </Badge>
          {subscriptionType && subscriptionType !== 'free' && (
            <img 
              src={trainerCertifiedBadge} 
              alt={config.certBadgeAlt}
              className="w-20 h-20 object-contain animate-pulse drop-shadow-xl z-20"
              title={config.certBadgeAlt}
            />
          )}
        </div>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <div>
          <h3 className="text-xl font-bold mb-2">{fullName}</h3>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <MapPin className="w-4 h-4" />
            <span>{locationCity}, {locationCountry}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {specialtyArray.slice(0, 3).map((spec, index) => (
            <Badge 
              key={index} 
              variant="secondary"
              className={`cursor-pointer ${config.hoverColor} transition-colors`}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`${config.directoryPath}?specialty=${encodeURIComponent(spec)}`);
              }}
            >
              {spec}
            </Badge>
          ))}
        </div>

        <p className="text-sm text-muted-foreground line-clamp-3">
          {bio}
        </p>

        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <Clock className={`w-4 h-4 ${config.iconColor}`} />
              <span>{yearsExperience} años</span>
            </div>
            {hourlyRate > 0 && (
              <div className="flex items-center gap-1">
                <Star className={`w-4 h-4 ${config.iconColor}`} />
                <span className="font-bold">{hourlyRate}€/h</span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                navigate(config.joinPath);
              }}
            >
              Únete
            </Button>
            <Button 
              size="sm" 
              className={type === "nutritionist" ? "bg-emerald-500 hover:bg-emerald-600" : 
                         type === "physiotherapist" ? "bg-rose-500 hover:bg-rose-600" : ""}
              onClick={() => navigate(profileLink)}
            >
              Ver Perfil
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
